import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Megaphone, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const iBookMessages = [
  {
    id: 'internal_1',
    brand_name: 'IBOOKSPORT',
    brand_color: '#ff4500',
    banner_text: 'sponsor.internalMsg1',
    banner_cta: 'sponsor.internalCta1',
    link_url: '/sponsoring',
    is_internal: true,
  },
  {
    id: 'internal_2',
    brand_name: 'SPORT ADS NETWORK',
    brand_color: '#ff4500',
    banner_text: 'sponsor.internalMsg2',
    banner_cta: 'sponsor.internalCta2',
    link_url: '/sponsoring',
    is_internal: true,
  },
];

const SponsorBanner = ({ eventId, sportType, region }) => {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const params = new URLSearchParams();
        if (eventId) params.append('event_id', eventId);
        if (sportType) params.append('sport_type', sportType);
        if (region) params.append('region', region);
        params.append('format', 'sidebar');
        const res = await api.get(`/sponsors/banners?${params.toString()}`);
        const sponsorBanners = res.data.banners || [];
        
        // Alternate: sponsor, internal, sponsor, internal...
        const mixed = [];
        let internalIdx = 0;
        for (let i = 0; i < sponsorBanners.length; i++) {
          mixed.push(sponsorBanners[i]);
          mixed.push({ ...iBookMessages[internalIdx % iBookMessages.length] });
          internalIdx++;
        }
        if (sponsorBanners.length === 0) {
          mixed.push({ ...iBookMessages[0] });
        }
        setBanners(mixed);
      } catch {
        setBanners([{ ...iBookMessages[0] }]);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, [eventId, sportType, region]);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % banners.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [banners.length]);

  const handleClick = useCallback(async (banner) => {
    if (banner.campaign_id) {
      try {
        await api.post('/sponsors/click', { campaign_id: banner.campaign_id, event_id: eventId, region });
      } catch {}
    }
    if (banner.link_url) {
      if (banner.is_internal) {
        window.location.href = banner.link_url;
      } else {
        window.open(banner.link_url, '_blank', 'noopener');
      }
    }
  }, [eventId, region]);

  if (loading || banners.length === 0) return null;

  const banner = banners[current];
  const isInternal = banner.is_internal;

  return (
    <div className="mt-6 overflow-hidden" data-testid="sponsor-banner">
      <div
        className="relative p-5 transition-all duration-500 cursor-pointer group"
        style={{
          background: isInternal
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1e293b 100%)'
            : `linear-gradient(135deg, ${banner.brand_color}10 0%, #f8fafc 50%, ${banner.brand_color}08 100%)`,
          borderLeft: `3px solid ${banner.brand_color || '#ff4500'}`,
        }}
        onClick={() => handleClick(banner)}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isInternal ? (
              <Megaphone className="w-4 h-4 text-brand" />
            ) : banner.logo_url ? (
              <img
                src={banner.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${banner.logo_url}` : banner.logo_url}
                alt={banner.brand_name}
                className="h-5 w-auto max-w-[80px] object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : null}
            <span
              className="font-heading font-bold uppercase tracking-[0.2em] text-[10px]"
              style={{ color: isInternal ? '#ff4500' : banner.brand_color }}
            >
              {banner.brand_name}
            </span>
          </div>
          <span className="text-[8px] text-slate-600 font-heading uppercase tracking-widest">
            {isInternal ? '' : t('sponsor.ad')}
          </span>
        </div>

        {/* Content */}
        <p className={`text-sm leading-relaxed mb-3 ${isInternal ? 'text-slate-300' : 'text-slate-600'}`}>
          {isInternal ? t(banner.banner_text) : banner.banner_text}
        </p>

        {/* CTA */}
        <div className="flex items-center justify-between">
          <span
            className="inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider transition-all group-hover:gap-2.5"
            style={{ color: banner.brand_color || '#ff4500' }}
          >
            {isInternal ? t(banner.banner_cta) : banner.banner_cta}
            {isInternal ? <ChevronRight className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
          </span>
          
          {/* Dots navigation */}
          {banners.length > 1 && (
            <div className="flex items-center gap-1">
              {banners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setCurrent(idx); }}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === current ? 'bg-brand w-4' : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SponsorBanner;
